import './styles/App.css';
import { AppProvider } from './contexts/AppContext';
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import OrderEdit from './pages/Customer/OrderEdit';
import {
  FULL_REGISTRATION_ROUTES,
  getExecutorRegistrationProgress,
} from './utils/executorRegistrationProgress';


// Компонент для прокрутки вверх при смене страницы
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant' // или 'smooth' для плавной прокрутки
    });
    
    // поддержка старых браузеров
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  return null;
}

function FullRegistrationRoute({ children }) {
  const { pathname, state } = useLocation();
  const requestedStep = FULL_REGISTRATION_ROUTES.indexOf(pathname);
  const progress = getExecutorRegistrationProgress();
  const lastAllowedStep = progress?.stepIndex ?? 0;

  if (
    requestedStep === 0 &&
    lastAllowedStep > 0 &&
    !state?.allowRegistrationBack
  ) {
    return <Navigate to={progress.path} replace />;
  }

  if (requestedStep > lastAllowedStep) {
    return <Navigate to={progress?.path || FULL_REGISTRATION_ROUTES[0]} replace />;
  }

  return children;
}

// Ленивая загрузка компонентов 
const Main = lazy(() => import('./pages/Main/Main'));
const Enter = lazy(() => import('./pages/Enter/Enter'));
const ShortStep1Phone = lazy(() => import('./pages/Registration/ShortRegistration/steps/ShortStep1Phone'));
const ShortStep2Name = lazy(() => import('./pages/Registration/ShortRegistration/steps/ShortStep2Name'));
const Step0Phone = lazy(() => import('./pages/Registration/FullRegistration/steps/Step0Phone'));
const Step0_1Branch = lazy(() => import('./pages/Registration/FullRegistration/steps/Step0_1Branch'));
const Step1Activity = lazy(() => import('./pages/Registration/FullRegistration/steps/Step1Activity'));
const Step2FullName = lazy(() => import('./pages/Registration/FullRegistration/steps/Step2FullName'));
const Step3Passport = lazy(() => import('./pages/Registration/FullRegistration/steps/Step3Passport'));
const Step4Experience = lazy(() => import('./pages/Registration/FullRegistration/steps/Step4Experience'));
const Step5Services = lazy(() => import('./pages/Registration/FullRegistration/steps/Step5Services'));
const Step6Contacts = lazy(() => import('./pages/Registration/FullRegistration/steps/Step6Contacts'));
const MainExecutor = lazy(() => import('./pages/Main/MainExecutor'));
const MainCustomer = lazy(() => import('./pages/Main/MainCustomer'));
const OtherCategories = lazy(() => import('./pages/Main/OtherCategories'));
const SpecificCategory = lazy(() => import('./pages/Main/SpecificCategory'));
const ExMyOrders = lazy(() => import('./pages/Executor/MyOrders'));
const AllOrders = lazy(() => import('./pages/Executor/AllOrders'));
const OrderDetail = lazy(() => import('./pages/Executor/OrderDetail'));
const CusMyOrders = lazy(() => import('./pages/Customer/MyOrders'));
const CreateOrderForm = lazy(() => import('./pages/Customer/CreateOrderForm'));
// const Catalog = lazy(() => import('./pages/Customer/Catalog'));
const AllSpecialists = lazy(() => import('./pages/Customer/AllSpecialists'));
const AllSpecialistsFilter = lazy(() => import('./pages/Customer/AllSpecialistsFilter'));
const ExecutorProfile = lazy(() => import('./pages/Customer/ExecutorProfile'));
const PersonalAccount = lazy(() => import('./pages/Customer/PersonalAccount'));
const EPersonalAccount = lazy(() => import('./pages/Executor/EPersonalAccount'));
const Error404 = lazy(() => import('./pages/Error404'));
const Anketa = lazy(() => import('./pages/Anketa'));
const Chat = lazy(() => import('./pages/Registration/common/Chat/Chat'));
const SupportChat = lazy(() => import('./pages/Registration/common/Chat/SupportChat'));


function App() {
  return (
    <Router>
      <AppProvider>
        <ScrollToTop />

        <Suspense fallback={<div>Загрузка...</div>}>
          <Routes>
            <Route path="/" element={<Main />} />
            <Route path="/enter" element={<Enter />} />
            <Route path="/simplified_registration_step1" element={<ShortStep1Phone />} />
            <Route path="/simplified_registration_step2" element={<ShortStep2Name />} />
            <Route path="/full_registration_step0" element={<FullRegistrationRoute><Step0Phone /></FullRegistrationRoute>} />
            <Route path="/full_registration_step0_1" element={<FullRegistrationRoute><Step0_1Branch /></FullRegistrationRoute>} />
            <Route path="/full_registration_step1" element={<FullRegistrationRoute><Step1Activity /></FullRegistrationRoute>} />
            <Route path="/full_registration_step2" element={<FullRegistrationRoute><Step2FullName /></FullRegistrationRoute>} />
            <Route path="/full_registration_step3" element={<FullRegistrationRoute><Step3Passport /></FullRegistrationRoute>} />
            <Route path="/full_registration_step4" element={<FullRegistrationRoute><Step4Experience /></FullRegistrationRoute>} />
            <Route path="/full_registration_step5" element={<FullRegistrationRoute><Step5Services /></FullRegistrationRoute>} />
            <Route path="/full_registration_step6" element={<FullRegistrationRoute><Step6Contacts /></FullRegistrationRoute>} />
            <Route path="/main_executor" element={<MainExecutor />} />
            <Route path="/main_customer" element={<MainCustomer />} />
            <Route path="/other_categories" element={<OtherCategories />} />
            <Route path="/category/:id" element={<SpecificCategory />} />
            <Route path="/executor_my_orders" element={<ExMyOrders />} />
            <Route path="/executor_all_orders" element={<AllOrders />} />
            <Route path="/order/:id" element={<OrderDetail />} />
            <Route path="/customer_order_edit/:orderId" element={<OrderEdit />} />
            <Route path="/customer_my_orders" element={<CusMyOrders />} />
            {/* <Route path="/customer_catalog" element={<Catalog />} /> */}
            <Route path="/customer_all_specialists" element={<AllSpecialists />} />
            <Route path="/executor_profile/:executorId" element={<ExecutorProfile />} />
            <Route path="/customer_all_specialists_filter" element={<AllSpecialistsFilter />} />
            <Route path="/customer_create_order" element={<CreateOrderForm />} />
            <Route path="/customer_personal_account" element={<PersonalAccount />} />
            <Route path="/executor_personal_account" element={<EPersonalAccount />} />
            <Route path="/error404" element={<Error404 />} />
            <Route path="/anketa" element={<Anketa />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/support/:ticketId" element={<SupportChat />} />

          </Routes>
        </Suspense>
      </AppProvider>
    </Router>
  );
}

export default App;
